/**
 * Unified auto-save status for the EVAR workflow.
 *
 * Both persistence hooks must stay mounted at all times — they own the
 * restore-from-server logic and the markDirty event subscriptions — so their
 * states are merged into a single indicator instead of rendering one chip per
 * section.
 */

import { AlertTriangle } from 'lucide-react';

import { SaveStatusIndicator } from '@/components/viewer/save-status-indicator';
import { mergePersistenceStates } from '@/hooks/use-auto-save';
import { useSizingPersistence } from '@/workflows/vascular/common/hooks/use-sizing-persistence';
import { useStrategyPersistence } from '@/workflows/vascular/evar/hooks/use-strategy-persistence';

export function EvarSaveStatus({ caseId }: { caseId: string }) {
    const sizing = useSizingPersistence({ caseId });
    const strategy = useStrategyPersistence({ caseId });
    const merged = mergePersistenceStates([sizing, strategy]);

    // Name the failed section(s): stale sizing and stale strategy data call
    // for different clinical double-checks, so a generic message won't do.
    const failedSections = [sizing.restoreError && 'sizing', strategy.restoreError && 'strategy']
        .filter((section): section is string => Boolean(section))
        .join(' and ');

    // A restore failure in one section must not hide the other section's live
    // save status, so the error renders alongside the indicator, not instead.
    return (
        <div className="flex items-center gap-3">
            {failedSections && (
                <div className="flex items-center gap-1.5 text-xs text-destructive">
                    <AlertTriangle className="h-3 w-3" />
                    Failed to restore {failedSections}
                </div>
            )}
            <SaveStatusIndicator
                status={merged.status}
                lastSavedAt={merged.lastSavedAt}
                retryCount={merged.retryCount}
                onSaveNow={merged.saveNow}
            />
        </div>
    );
}
